import CaregiverProfileModal from 'components/modals/CaregiverProfileModal';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import {
  QUALIFICATION_LEVELS,
  pickCaregivers,
  qualificationLevelOf,
  useRegisteredCaregivers,
} from 'hooks/useCaregivers';
import { ArrowRightIcon } from 'lucide-react';
import posthog from 'posthog-js';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';
import { RegisteredCaregiverDTO } from 'services/api/models/RegisteredCaregiverDTO';
import CaregiverCard from './CaregiverCard';

/** Caregiver cards in two columns, each opening the caregiver's profile. */
export function CaregiverGrid({ caregivers }: { caregivers: RegisteredCaregiverDTO[] }) {
  const [openedCaregiverId, setOpenedCaregiverId] = useState<string | null>(null);

  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        {caregivers.map(caregiver => (
          <CaregiverCard
            key={caregiver.user.id}
            caregiver={caregiver}
            onClick={() => {
              posthog.capture('messaging.caregiver_profile_opened');
              setOpenedCaregiverId(caregiver.user.id);
            }}
          />
        ))}
      </div>
      {openedCaregiverId && (
        <CaregiverProfileModal caregiverId={openedCaregiverId} open onClose={() => setOpenedCaregiverId(null)} />
      )}
    </>
  );
}

/** Caregivers shown per category, at random and of different specialties. */
const CAREGIVERS_PER_LEVEL = 2;

/**
 * « Gynger c'est aussi toute une équipe à vos côtés »: a few caregivers of
 * each category, drawn at random, never two of the same specialty.
 */
export default function TeamSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: caregivers, isPending } = useRegisteredCaregivers();

  return (
    <section className="flex flex-col gap-8" data-testid="messaging-team-section">
      <h2>
        <Trans i18nKey="messagingV3.team.title" components={{ br: <br /> }} />
      </h2>
      {isPending ? (
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-52" />
          <Skeleton className="h-52" />
        </div>
      ) : (
        QUALIFICATION_LEVELS.map(level => {
          const inLevel = pickCaregivers(
            (caregivers ?? []).filter(c => qualificationLevelOf(c) === level),
            CAREGIVERS_PER_LEVEL,
          );
          if (inLevel.length === 0) return null;
          return (
            <div key={level} className="flex flex-col gap-4">
              <p className="text-2xs font-bold tracking-widest text-subtle-foreground uppercase">
                {t(`messagingV3.team.${level}`)}
              </p>
              <CaregiverGrid caregivers={inLevel} />
            </div>
          );
        })
      )}
      <Button variant="outline" className="self-center" onClick={() => navigate(routes.messagingTeam.route)}>
        {t('messagingV3.team.seeAll')}
        <ArrowRightIcon />
      </Button>
    </section>
  );
}
